import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  DatePicker,
  Input,
  Select,
  TimeInput,
  Toggle,
  EditableText,
  Badge,
  Expanded,
  DateTimePicker,
} from "../../../../shared";
import { renderLabelRequired } from "../../../../shared/Table/table.utils";
import { useGetDistanceMutation } from "../../../../redux/services/map/mapApi";
import {
  formatDate,
  padInt,
  padTime,
  parseFixed,
  renderAmount,
  updateDate,
  updateTime,
} from "../../../../utils";
import WaitTime from "./WaitTime";
import { getMilesFromMeters, renderTotalMileage } from "../../jobs.utils";
import { loadTypeOptions } from "../../select.config";
import { useConfig } from "../../../../hooks/useConfig";
import { Metadata } from "./Metadata";
import LocationDropdown from "../../../../components/LocationDropdown";
import LegMileageSelector from "./LegMileageSelector";
import { useGetTemplatesQuery } from "../../../../redux/services/templates/api";
import {
  getTemplateContent,
  templateMatchesAppType,
} from "../../../Settings/Templates/template.utils";
import { DriverSelect } from "../../../../components/DriverSelect";

export const LegCreateForm = ({
  remove,
  id,
  formData = {},
  data = {},
  job,
  renderId = (id) => id,
  handleChange,
  errors = {},
  renderValue,
  form = {},
  driverOptions = [],
  locationOptions = [],
  setForm,
  addForm,
  formId,
  title,
  size = "md",
  jobCreate = false,
  jobCreateLayout = false,
  toggleHandoff,
  hideHeader = false,
  onMileageUpdate,
}) => {
  const legId = formId;
  const legForm = formData;
  const { configs, appType } = useConfig();
  const { data: legTemplateData, isLoading: legTemplatesLoading } =
    useGetTemplatesQuery(
      {
        type: "leg",
        limit: 50,
      },
      { skip: jobCreate }
    );

  const legTemplateOptions =
    legTemplateData?.templates
      ?.filter((template) => template.type === "leg")
      .filter((template) => templateMatchesAppType(template, appType))
      .map((template) => ({
        label: template.name,
        value: template._id,
        template,
      })) || [];

  // ✅ Helper function to extract appointment time (moved before useEffect)
  function renderAppointmentTime(date, time) {
    if (time) {
      return time;
    }

    // ✅ FIX: Extract time from date in 12-hour AM/PM format
    if (date) {
      const d = new Date(date);
      if (isNaN(d.getTime())) return null;

      return d.toLocaleTimeString("en-US", {
        hour12: true,
        hour: "numeric",
        minute: "2-digit",
      });
    }

    return null;
  }

  // ✅ Initialize appointmentTime from appointmentDate when loading existing legs
  useEffect(() => {
    const currentAppointmentDate = form?.appointmentDate;
    const currentAppointmentTime = form?.appointmentTime;

    // If we have an appointmentDate but no appointmentTime, extract it
    if (currentAppointmentDate && !currentAppointmentTime) {
      const extractedTime = renderAppointmentTime(currentAppointmentDate, null);
      if (extractedTime) {
        handleChange({ id: "appointmentTime", value: extractedTime });
      }
    }
  }, [form?.appointmentDate, form?.appointmentTime, handleChange]);

  // RTK Query
  const [getDistance] = useGetDistanceMutation();

  // State for time range toggle
  const [useTimeRange, setUseTimeRange] = useState(false);

  // Ref to prevent duplicate distance API calls for the same route
  const lastCalculatedRouteRef = useRef(null);

  // Add debugging wrapper for handleChange

  // Removed problematic useEffect that was causing infinite re-render loop
  // The form already receives totalMiles from parent state and doesn't need to trigger updates

  // Get value
  function getValue(valueId) {
    if (jobCreate) {
      return renderValue(valueId);
    } else {
      const result = Object.byString(form, valueId);

      return result;
    }
  }

  // Set values as variables
  const isEditing = getValue("edit");
  const orderNumber = getValue("orderNumber");
  const loadType = getValue("loadType");
  const origin = getValue("origin");
  const destination = getValue("destination");
  const driver = getValue("driver.fullName"); //?.fullName || form.driver?.fullName;
  const start = getValue("waitStart");
  const end = getValue("waitEnd");
  const legFuelSurcharge = getValue("fuelSurcharge") || 0;
  const useCustomFuelSurcharge = getValue("useCustomFuelSurcharge");
  const baseCharge = getValue("baseCharge") || 0;
  const useCustomBaseCharge = getValue("useCustomBaseCharge");
  const dropCharge = getValue("dropCharge") || 0;
  const useCustomDropCharge = getValue("useCustomDropCharge");
  const otherCharges = getValue("otherCharges") || 0;
  const useCustomOtherCharges = getValue("useCustomOtherCharges");
  const weight = getValue("weight") || 0;
  const appointmentDate = getValue("appointmentDate");
  const appointmentTime = renderAppointmentTime(
    appointmentDate,
    getValue("appointmentTime")
  );

  const timeRange = getValue("timeRange");
  const totalMiles = getValue("totalMiles");
  const totalCharges = getValue("totalCharges");
  const hasHandoff = getValue("hasHandoff");
  const isHandoff = getValue("isHandoff");
  const useLegFlatRate = getValue("useLegFlatRate");
  const useGlobalRates = getValue("job.useGlobalRates");
  const useFlatRate = getValue("job.useFlatRate");
  const totalWaitTime = getValue("totalWaitTime");
  const defaultMileage = getValue("defaultMileage");
  const customMileage = getValue("customMileage");
  const useCustomMileage = getValue("useCustomMileage");
  const billedMiles = getValue("billedMiles");
  const mileageSelectionType = getValue("mileageSelectionType");
  const route = getValue("route");
  const routeDefaultMileage = route?.defaultMileage ?? route?.mileage ?? null;
  const resolvedDefaultMileage =
    defaultMileage != null ? defaultMileage : routeDefaultMileage;
  const resolvedCustomMileage =
    customMileage != null ? customMileage : route?.customMileage ?? null;
  const resolvedBilledMiles =
    billedMiles != null ? billedMiles : route?.billedMiles ?? null;
  const normalizedMileageSelectionType = String(mileageSelectionType || "")
    .toLowerCase()
    .trim();
  const hasExplicitCustomMileageSelection = [
    "billedmileage",
    "billedmiles",
    "custominput",
    "currentmileage",
  ].includes(normalizedMileageSelectionType);
  const hasLegacyCustomMileageSelection =
    normalizedMileageSelectionType === "" &&
    resolvedDefaultMileage != null &&
    totalMiles != null &&
    Number(totalMiles) > 0 &&
    Number(totalMiles) !== Number(resolvedDefaultMileage);
  const shouldTreatAsCustomMileage =
    !!useCustomMileage ||
    hasExplicitCustomMileageSelection ||
    hasLegacyCustomMileageSelection;
  const effectiveCustomMileage =
    shouldTreatAsCustomMileage && Number(totalMiles) > 0
      ? resolvedCustomMileage ??
        resolvedBilledMiles ??
        Number(totalMiles) ??
        route?.customMileage ??
        route?.billedMiles ??
        null
      : resolvedCustomMileage;

  // Determine if we should show time range picker
  const isServiceRepair = appType === "serviceRepair";
  const shouldShowTimeRange = isServiceRepair && useTimeRange;

  // Initialize time range toggle based on existing data
  useEffect(() => {
    if (isServiceRepair && timeRange && (timeRange.start || timeRange.end)) {
      setUseTimeRange(true);
    }
  }, [isServiceRepair, timeRange]);

  // Function to get form leg by id or form
  function renderFormLeg() {
    if (jobCreate) {
      return form.legs?.[legId] || {};
    }
    return form || {};
  }

  // console.log("🔍 Form:", form);
  // console.log("🔍 Job:", job);
  // Function to set form data
  async function setFormData(newData) {
    // console.log("🔍 New Data:", newData);
    let initialData = {
      ...form,
      ...newData,
      loadNumber: job?.loadNumber,
    };

    // console.log("🚛 Frontend: Setting form data with loadNumber:", {
    //   loadNumber: form.job?.loadNumber,
    //   hasJob: !!form.job,
    //   jobId: form.job?._id,
    //   jobLoadNumber: form.job?.loadNumber,
    //   newDataLoadNumber: newData.loadNumber,
    // });

    if (jobCreate) {
      initialData.legs = {
        ...form.legs,
        // [legId]: newData,
        ...newData,
      };
    } else {
      initialData = { ...newData };
    }

    await setForm(initialData, true);
  }

  function handleApplyLegTemplate(selected) {
    const template = selected?.data?.template;
    if (!template) return;

    const content = getTemplateContent(template);
    const defaults = content.defaults || content;
    setFormData({
      ...renderFormLeg(),
      ...defaults,
    });
  }

  function renderRate(rateId, id) {
    if (form.job?.[rateId]) {
      return form.job[rateId];
    }

    let rateBases = { client: form.client?.rates, configs: configs?.rates };
    let rateBase = "client";

    let res = rateBases[rateBase]?.[rateId];

    if (res === undefined || useGlobalRates) {
      rateBase = "configs";
      res = rateBases[rateBase]?.[rateId];
    }

    return res;
  }

  /*
    Render leg baseCharge, dropCharge, fuelSurcharge, dropCharge
    when setting loadType
  */
  useEffect(() => {
    if (loadType) {
      const loadTypeId = loadType?.toLowerCase();
      const loadTypeCharge = renderRate(loadTypeId, "loadType");
      const update = {
        ...renderFormLeg(),
      };

      const dropCharge = useFlatRate ? null : loadTypeCharge || 0;

      if (useLegFlatRate) {
        update[renderId("baseCharge")] = renderDropCharge(dropCharge);
        update[renderId("dropCharge")] = null;
        update[renderId("fuelSurcharge")] = null;
      } else {
        update[renderId("dropCharge")] = renderDropCharge(dropCharge);
      }

      if (jobCreate) {
        // For job create, update via onMileageUpdate to avoid form reset
        if (typeof onMileageUpdate === "function") {
          onMileageUpdate(totalMiles || 0, update);
        }
      } else {
        setFormData(update);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadType]);

  /*
    Render leg totalMiles, defaultMileage, customMileage, useCustomMileage, fuelSurcharge, baseCharge
    when setting custom mileage
  */
  function setCustomDistance(miles, doNotSet) {
    // Get existing route-derived default mileage - do NOT overwrite it
    const existingDefaultMileage = getValue("defaultMileage") || totalMiles;
    
    const newData = {
      ...renderFormLeg(),
      // Keep totalMiles as the billed value for display/calculations
      [renderId("totalMiles")]: miles,
      // Canonical payload fields used by API
      [renderId("mileageUsed")]: miles,
      [renderId("mileageSelectionType")]: "billedMileage",
      // Preserve the original route-derived default mileage
      [renderId("defaultMileage")]: existingDefaultMileage,
      // Store the custom/billed mileage separately
      [renderId("customMileage")]: miles,
      [renderId("billedMiles")]: miles,
      [renderId("useCustomMileage")]: true,
    };

    if (!useFlatRate && !useLegFlatRate) {
      newData[renderId("fuelSurcharge")] = renderFuelSurcharge(miles);
      newData[renderId("baseCharge")] = renderBaseCharge(miles);
    }

    !doNotSet && setFormData(newData);
    return newData;
  }

  function applyMileageUpdate(miles, data) {
    if (jobCreate && typeof onMileageUpdate === "function") {
      onMileageUpdate(miles, data);
      return;
    }
    setFormData(data);
  }

  function handleMileageSelection(e) {
    const rawValue = e?.value ?? e;
    const isObjectValue = rawValue && typeof rawValue === "object";
    const mileageData = isObjectValue ? rawValue : null;
    const selectedMiles = Number(
      isObjectValue ? mileageData.value : rawValue
    );

    if (Number.isNaN(selectedMiles) || selectedMiles < 0) return;

    const isCustomSelection =
      !isObjectValue ||
      mileageData.mileageType === "customInput" ||
      mileageData.mileageType === "billedMileage" ||
      mileageData.mileageType === "billedMiles" ||
      mileageData.mileageType === "currentMileage";

    if (isCustomSelection) {
      const update = setCustomDistance(selectedMiles, true);
      applyMileageUpdate(selectedMiles, update);
      return;
    }

    const update = {
      ...renderFormLeg(),
      [renderId("totalMiles")]: selectedMiles,
      // Canonical payload fields used by API
      [renderId("mileageUsed")]: selectedMiles,
      [renderId("mileageSelectionType")]: "defaultMileage",
      [renderId("useCustomMileage")]: false,
      [renderId("customMileage")]: null,
    };

    if (defaultMileage == null || defaultMileage <= 0) {
      update[renderId("defaultMileage")] = selectedMiles;
    }

    if (!useFlatRate && !useLegFlatRate) {
      update[renderId("fuelSurcharge")] = renderFuelSurcharge(selectedMiles);
      update[renderId("baseCharge")] = renderBaseCharge(selectedMiles);
    }

    applyMileageUpdate(selectedMiles, update);
  }

  function renderMileageEditValue() {
    return {
      value: Number(totalMiles || 0),
      isRouteMileage: !shouldTreatAsCustomMileage,
      mileageType: shouldTreatAsCustomMileage
        ? mileageSelectionType || "billedMileage"
        : "defaultMileage",
    };
  }

  /**
   * Memoized mileage selector layout for EditableText custom type.
   * IMPORTANT: This must NOT be an inline function component, otherwise React
   * treats every rerender of LegCreateForm as a new component type, unmounting
   * and remounting LegMileageSelector (closing dropdown, resetting state).
   * Instead we memoize a stable render function that returns JSX.
   */
  const mileageSelectorRouteData = useMemo(
    () => ({
      defaultMileage: resolvedDefaultMileage,
      mileage: totalMiles,
      customMileage: effectiveCustomMileage,
      billedMiles: resolvedBilledMiles,
    }),
    [
      resolvedDefaultMileage,
      totalMiles,
      effectiveCustomMileage,
      resolvedBilledMiles,
    ]
  );

  const MileageSelectorLayout = useCallback(
    (layoutProps) => (
      <LegMileageSelector
        route={mileageSelectorRouteData}
        totalMiles={Number(totalMiles || 0)}
        useCustomMileage={shouldTreatAsCustomMileage}
        customMileage={effectiveCustomMileage}
        mileageSelectionType={mileageSelectionType}
        onChange={layoutProps.onChange}
        size={size}
        showLabel={false}
      />
    ),
    [
      mileageSelectorRouteData,
      totalMiles,
      shouldTreatAsCustomMileage,
      effectiveCustomMileage,
      mileageSelectionType,
      size,
    ]
  );

  /*
    Render leg total miles, fuelSurcharge, baseCharge
    Use existing totalMiles from extraction if available, otherwise calculate distance
    Prevents duplicate API calls by tracking the last calculated route
  */
  useEffect(() => {
    if (!form.useCustomMileage && origin?._id && destination?._id) {
      // Create a stable route key to track if we've already calculated for this pair
      const routeKey = `${origin._id}-${destination._id}`;

      // Check if totalMiles is already set from extraction (has valid value > 0)
      const existingMiles = totalMiles && totalMiles > 0 ? totalMiles : null;
      const routeOriginId =
        route?.origin?._id || route?.origin?.id || route?.origin || null;
      const routeDestinationId =
        route?.destination?._id ||
        route?.destination?.id ||
        route?.destination ||
        null;
      const hasPreResolvedRouteForPair =
        !!route?._id &&
        String(routeOriginId) === String(origin._id) &&
        String(routeDestinationId) === String(destination._id);
      const hasMilesForRoute =
        lastCalculatedRouteRef.current === routeKey && existingMiles;

      // If extraction already provided a route + mileage for this pair, treat it as calculated.
      if (
        hasPreResolvedRouteForPair &&
        existingMiles &&
        lastCalculatedRouteRef.current !== routeKey
      ) {
        lastCalculatedRouteRef.current = routeKey;
        return;
      }

      // If we already calculated for this route and have miles, skip
      if (hasMilesForRoute) {
        return;
      }

      // Calculate distance when route changes or mileage is missing
      (async () => {
        try {
          const res = await getDistance({
            origin: {
              _id: origin._id,
              address: origin.address.fullAddress,
            },
            destination: {
              _id: destination._id,
              address: destination.address.fullAddress,
            },
          }).unwrap();

          if (res.status === "success") {
            const computedDefaultMiles = renderTotalMiles(
              getMilesFromMeters(res.data.distance.value),
              true
            );
            const adjustedMiles =
              res.data.distance.adjusted != null
                ? renderTotalMiles(res.data.distance.adjusted, true)
                : null;
            const apiBilledMiles =
              res.data?.billedMiles != null ? Number(res.data.billedMiles) : null;
            const resolvedBilledMiles =
              apiBilledMiles != null && !Number.isNaN(apiBilledMiles)
                ? apiBilledMiles
                : adjustedMiles;
            const hasBilledMileageFromRoute =
              resolvedBilledMiles != null &&
              resolvedBilledMiles > 0 &&
              computedDefaultMiles > 0 &&
              Number(resolvedBilledMiles) !== Number(computedDefaultMiles);
            const miles = shouldTreatAsCustomMileage
              ? effectiveCustomMileage ??
                resolvedBilledMiles ??
                adjustedMiles ??
                computedDefaultMiles
              : computedDefaultMiles;

            const data = {
              ...renderFormLeg(),
              [renderId("totalMiles")]: miles,
              [renderId("mileageUsed")]: miles,
              [renderId("defaultMileage")]: computedDefaultMiles || miles,
              [renderId("fullRouteData")]: res.data.setFullRouteData && {
                distance: res.data.distance.value,
                distanceText: res.data.distance.text,
                duration: res.data.duration.value,
                durationText: res.data.duration.text,
              },
            };
            if (resolvedBilledMiles != null && resolvedBilledMiles > 0) {
              data[renderId("billedMiles")] = resolvedBilledMiles;
            }
            // Keep user-selected mode authoritative in create/edit flows.
            // Distance fetch should populate available mileage values, not force
            // a selection type for legs that are still on default mileage.
            if (shouldTreatAsCustomMileage && hasBilledMileageFromRoute) {
              data[renderId("customMileage")] =
                effectiveCustomMileage ?? resolvedBilledMiles;
            }
            if (res.data?.computedMilesExact != null) {
              data[renderId("computedMilesExact")] = res.data.computedMilesExact;
            }

            if (!useLegFlatRate || !useFlatRate) {
              data[renderId("fuelSurcharge")] = renderFuelSurcharge(miles);
            }
            if (!useFlatRate) {
              data[renderId("baseCharge")] = renderBaseCharge(miles);
            }

            if (!jobCreate) {
              setFormData(data);
            }

            // Notify parent that mileage has been updated
            if (typeof onMileageUpdate === "function") {
              onMileageUpdate(miles, data);
            }

            // Mark this route as calculated
            lastCalculatedRouteRef.current = routeKey;
          }
        } catch (error) {
          console.error("Error calculating distance:", error);
        }
      })();
    }
    // Use stable references (IDs) instead of objects to prevent unnecessary rerenders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    origin?._id,
    destination?._id,
    form.useCustomMileage,
    form.useLegFlatRate,
    // Don't include totalMiles in dependencies - it's checked inside the effect
    // Including it causes loops when totalMiles updates trigger the effect again
  ]);

  /*
    Render leg base charge after leg flat rate change
  */
  useEffect(() => {
    if (form.useLegFlatRate) {
      handleChange({ baseCharge: renderBaseCharge(baseCharge) });
    }
  }, [form.useLegFlatRate]);

  /*
    Render leg total fuelSurcharge
  */
  useEffect(() => {
    const newData = {
      ...renderFormLeg(),
      [renderId("fuelSurcharge")]: renderFuelSurcharge(
        totalMiles,
        renderRate("fuelRate") //form.fuelRate
      ),
    };
    setFormData(newData);
  }, [form.fuelRate]);

  /*
    Render leg total charges.
    Only dispatch handleChange when the computed total actually differs from
    the current value to prevent unnecessary parent state updates (which
    cascade rerenders to ALL legs and can unmount the MileageSelector).
  */
  const prevTotalChargesRef = useRef(totalCharges);
  useEffect(() => {
    const total = parseFixed(renderTotalCharges());
    // Guard: skip update if the total hasn't changed to avoid re-render cascade
    if (total === prevTotalChargesRef.current) return;
    prevTotalChargesRef.current = total;
    handleChange({ id: "totalCharges", value: total });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legFuelSurcharge, baseCharge, dropCharge, otherCharges, totalMiles]);

  /*
    Render leg total charges
  */
  function renderTotalCharges() {
    if (useFlatRate || useLegFlatRate) {
      return Number(otherCharges) + Number(baseCharge);
    }

    const total =
      Number(legFuelSurcharge) +
      Number(baseCharge) +
      Number(dropCharge) +
      Number(otherCharges);

    return total;
  }

  /*
    Render leg total miles
  */
  function renderTotalMiles(totalMiles, reset) {
    if (reset) {
      return totalMiles;
    }
    if (form.useCustomMileage) return form.customMileage;
    return totalMiles;
  }

  /*
    Render leg base charge
  */
  function renderBaseCharge(totalMiles) {
    if (useFlatRate) return 0;
    if (useCustomBaseCharge || useLegFlatRate) return baseCharge;
    const baseRate =
      getValue("job.baseRate") || renderRate("baseRate") || renderRate("base"); // || 0;

    const mileage = totalMiles || 0;
    const res = parseFixed(Number(mileage) * Number(baseRate));
    return res;
  }

  /*
    Render leg fuel charge
  */
  function renderFuelSurcharge(totalMiles, charge) {
    if (useFlatRate || useLegFlatRate) return 0;
    if (useCustomFuelSurcharge) return legFuelSurcharge;
    const fuelRate =
      charge ||
      getValue("job.fuelRate") ||
      renderRate("fuelRate") ||
      renderRate("fuel");
    const mileage = totalMiles || 0;
    return parseFixed(Number(mileage) * Number(fuelRate));
  }

  /*
    Render leg drop charge
  */
  function renderDropCharge(charge) {
    if (useFlatRate) return 0;
    if (useCustomDropCharge) return dropCharge;
    return parseFixed(charge);
  }

  /*
    Render leg other charges
  */
  function renderOtherCharges(waitTime = 0) {
    const waitCalc = waitTime - (configs?.job?.waitTimeOffset || 0);
    const waitCharge = waitCalc <= 0 ? 0 : waitCalc * renderRate("standBy");

    return waitCharge;

    // if (useFlatRate || useLegFlatRate) {
    //   if (useCustomOtherCharges) return otherCharges;
    //   return Number(totalWaitTime) + Number(charge || 0) || 0;
    // }
    // return parseFixed(otherCharges);
  }

  /*
    Handle handoff
  */
  function handleHandoff({ target }) {
    toggleHandoff({ id: target.id, value: target.checked });
    // if (!jobCreate) {
    //   return;
    // }
    // const newKey = "leg_" + (id + 1);
    // if (target.checked) {
    //   const initData = {
    //     [`${newKey}_orderNumber`]: orderNumber,
    //     [`${newKey}_loadType`]: loadType,
    //     [`${newKey}_weight`]: weight,
    //     [`${newKey}_appointmentDate`]: appointmentDate,
    //     [`${newKey}_origin`]: destination,
    //     [`${newKey}_isHandoff`]: true,
    //   };
    //   addForm(initData, { init: true });
    // } else {
    //   remove(newKey);
    // }
  }

  function handleUseLegFlatRate(e) {
    let { id, checked } = e.target;
    let finalData;
    // const oldLeg = renderFormLeg();

    // const newLeg = Object.keys(oldLeg)
    //   .filter((i) => {
    //     if (i.includes("loadType") || i.includes("baseCharge")) {
    //       return false;
    //     }
    //     if (checked) {
    //       if (i.includes("fuelSurcharge") || i.includes("dropCharge")) {
    //         return false;
    //       }
    //     }
    //     return true;
    //   })
    //   .map((i) => ({ [i]: oldLeg[i] }));

    // const finalLeg = Object.assign({}, ...newLeg);
    finalData = {
      ...renderFormLeg(),
      [id]: checked,
      dropCharge: 0,
      loadType: null,
    };

    // Check if value is false and rerender baseCharge
    if (!checked) {
      finalData.baseCharge = renderBaseCharge(totalMiles);
      finalData.fuelSurcharge = renderFuelSurcharge(totalMiles);
    } else {
      if (useCustomBaseCharge) {
        finalData.baseCharge = baseCharge;
      } else {
        finalData.baseCharge = 0;
      }
    }

    setFormData(finalData, true);
  }

  function renderLoadTypeOptions() {
    return loadTypeOptions.filter((item) => {
      if (useLegFlatRate && item.base) {
        return true;
      }
      if (!useLegFlatRate && !item.base) {
        return true;
      }
      return false;
    });
  }

  function routeChange(e) {
    const res = {
      ...form,
      [e.id]: e.value,
      mileageSelectionType: "defaultMileage",
      useCustomMileage: false,
      useCustomDropCharge: false,
      useCustomOtherCharges: false,
      useCustomFuelSurcharge: false,
      useCustomBaseCharge: false,
    };
    setFormData(res);
  }

  function handleCustomCharge(e) {
    const res = { ...renderFormLeg(), [e.id]: e.value, [e.type]: true };
    setFormData(res);
  }

  function handleRouteLocationChange(id, value) {
    // Normalize Select payloads to the actual location object
    const normalizedValue = value?.value ?? value;
    handleChange({ id: renderId(id), value: normalizedValue });

    if (form.useCustomMileage) {
      handleChange({ id: renderId("useCustomMileage"), value: false });
      handleChange({ id: renderId("customMileage"), value: null });
      handleChange({ id: renderId("billedMiles"), value: null });
      handleChange({
        id: renderId("mileageSelectionType"),
        value: "defaultMileage",
      });
    }
  }

  function handleApptDate(e) {
    // ✅ FIX: Don't try to preserve existing appointment time
    // The DateTimePicker already combines date and time correctly
    // Just use the combined date/time that was passed to us

    // Extract the time from the new date for the appointmentTime field
    if (e instanceof Date && !isNaN(e.getTime())) {
      const timeString = e.toLocaleTimeString("en-US", {
        hour12: true,
        hour: "numeric",
        minute: "2-digit",
      });

      // Update both the appointment date and time
      handleChange({ id: "appointmentTime", value: timeString });
      handleChange({ id: "appointmentDate", value: e });
    } else {
      handleChange({ id: "appointmentDate", value: null });
    }
  }

  function handleApptTime(value) {
    // console.log(
    //   "⚠️ handleApptTime called (not used by DateTimePicker):",
    //   value
    // );

    const apptDate = form.appointmentDate || new Date(); // ✅ Fallback to today if no date set
    const newTime = updateTime(apptDate, value);

    // ✅ CRITICAL: Store the appointment time for future date changes
    handleChange({ id: "appointmentTime", value });
    handleChange({ id: "appointmentDate", value: newTime });
  }

  // Handle time range changes
  function handleTimeRangeChange(rangeValue) {
    // console.log("handleTimeRangeChange", rangeValue);
    handleChange({ id: "timeRange", value: rangeValue });
  }

  // Handle time range toggle
  function handleTimeRangeToggle(e) {
    const { checked } = e.target;
    setUseTimeRange(checked);

    if (checked) {
      // Initialize time range with current appointment date
      const currentDate = appointmentDate || new Date();
      const startDate = new Date(currentDate);
      const endDate = new Date(currentDate);
      endDate.setHours(startDate.getHours() + 2); // Default 2 hours duration

      handleChange({
        id: "timeRange",
        value: {
          start: startDate,
          end: endDate,
        },
      });
    } else {
      // Clear time range and use appointment date
      handleChange({ id: "timeRange", value: null });
    }
  }

  // Render the appropriate date/time picker based on app type and settings
  function renderDateTimePicker() {
    if (shouldShowTimeRange) {
      return (
        <DateTimePicker
          range={true}
          id={renderId("timeRange")}
          value={timeRange}
          onChange={handleTimeRangeChange}
          labels={{
            startDate: "Start Date",
            startTime: "Start Time",
            endDate: "End Date",
            endTime: "End Time",
          }}
          size={size}
        />
      );
    }

    return (
      <DateTimePicker
        id={renderId("appointmentDate")}
        value={appointmentDate} // ✅ FIX: Pass full appointmentDate (includes time)
        placeholder="Date"
        label="Appointment Date"
        onChange={handleApptDate}
        size={size}
      />
    );
  }

  // NOTE: Duplicate appointment time useEffect removed — already handled at top of component (line ~82)

  return (
    <div className="mx-1 col-span-2 md:col-span-1 xl:col-span-2 space-y-4">
      {!hideHeader && (
        <div className="flex items-center justify-between pb-4">
          <div className="flex items-center">
            {(isHandoff || hasHandoff) && (
              <Badge label={isHandoff ? "Handoff" : "Has Handoff"} />
            )}
          </div>
          <div className="flex space-x-2">
            <Toggle
              // disabled={useFlatRate}
              id={renderId("useLegFlatRate")}
              label="Flat Rate"
              size="sm"
              checked={useLegFlatRate}
              onChange={handleUseLegFlatRate}
            />
            {isServiceRepair && (
              <Toggle
                id={renderId("useTimeRange")}
                label="Time Range"
                size="sm"
                checked={useTimeRange}
                onChange={handleTimeRangeToggle}
              />
            )}
            {!isHandoff && toggleHandoff && (
              <Toggle
                id={renderId("hasHandoff")}
                label="Handoff"
                size="sm"
                checked={hasHandoff}
                onChange={handleHandoff}
                disabled={isEditing && hasHandoff}
              />
            )}
          </div>
        </div>
      )}
      {!jobCreate && !isEditing && legTemplateOptions.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <Select
            label="Apply Leg Template"
            placeholder="Select a leg template"
            value=""
            loading={legTemplatesLoading}
            options={legTemplateOptions}
            onChange={handleApplyLegTemplate}
            size={size}
          />
        </div>
      )}
      <div
        className={`grid gap-2 grid-cols-2 ${
          jobCreateLayout
            ? "xl:grid-cols-4 max-w-2xl mx-auto"
            : "xl:grid-cols-6"
        }`}
      >
        <div
          className={`col-span-2 md:col-span-1 ${
            jobCreateLayout ? "xl:col-span-1" : "xl:col-span-2"
          }`}
        >
          <Input
            id={renderId("orderNumber")}
            size={size}
            value={orderNumber}
            onChange={handleChange}
            placeholder="Order #"
            label={renderLabelRequired("Order #")}
            error={errors[renderId("orderNumber")]}
          />
        </div>
        <Select
          size={size}
          id={renderId("loadType")}
          value={loadType}
          onChange={handleChange}
          label="Load Type"
          options={renderLoadTypeOptions()}
          placeholder="Select Load Type"
        />
        <Input
          size={size}
          type="number"
          min="0"
          id={renderId("weight")}
          value={weight}
          onChange={handleChange}
          placeholder="Weight"
          label="Weight"
          icon={<div className="text-gray-500 text-sm">lbs</div>}
        />
        <div
          className={`col-span-2 md:col-span-1 ${
            jobCreateLayout ? "xl:col-span-1" : "xl:col-span-2"
          }`}
        >
          <DriverSelect
            size={size}
            id={renderId("driver")}
            value={driver}
            onChange={handleChange}
            label="Driver"
            options={driverOptions}
            placeholder="Select Driver"
          />
        </div>
        <div className="col-span-2 sm:col-span-1 xl:col-span-2">
          <LocationDropdown
            size={size}
            id={renderId("origin")}
            clientId={form.client?._id}
            value={origin?.address?.fullAddress}
            onChange={(e) => handleRouteLocationChange("origin", e)}
            label="Origin"
            createModalDisplayMode={jobCreateLayout ? "side" : "center"}
          />
        </div>
        <div className="col-span-2 sm:col-span-1 xl:col-span-2">
          <LocationDropdown
            size={size}
            id={renderId("destination")}
            clientId={form.client?._id}
            value={destination?.address?.fullAddress}
            onChange={(e) => handleRouteLocationChange("destination", e)}
            label="Destination"
            createModalDisplayMode={jobCreateLayout ? "side" : "center"}
          />
        </div>

        <div className="col-span-2">{renderDateTimePicker()}</div>

        <div className="col-span-2 xl:col-span-2">
          <WaitTime
            size={size}
            start={start}
            end={end}
            onFormChange={handleChange}
            legId={legId}
            legForm={legForm}
            form={form}
            renderId={renderId}
            setForm={setFormData}
            renderValue={getValue}
            renderRate={renderRate}
            renderFormLeg={renderFormLeg}
            handleChange={handleChange}
            renderOtherCharges={renderOtherCharges}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3 mt-6">
        <div className="col-span-3 md:col-span-2 xl:col-span-1 flex flex-col space-y-1">
          <span className="block mb-2 text-sm font-medium text-neutral-700">
            Total Mileage
          </span>
          <EditableText
            className="ml-1"
            iconClasses="h-3 w-3"
            id="totalMiles"
            value={renderMileageEditValue()}
            size={size}
            type="custom"
            displayText={() => (
              <span className="font-semibold text-xl">
                {renderTotalMileage(totalMiles)}
              </span>
            )}
            onSave={(saveData) => handleMileageSelection(saveData)}
            layout={MileageSelectorLayout}
          />
        </div>
        {!useFlatRate && (
          <CalculationsLayout
            label="Base Charge"
            value={baseCharge}
            editable={true}
            id="baseCharge"
            onSave={(saveData) =>
              handleCustomCharge({
                id: renderId("baseCharge"),
                value: Number(saveData.value),
                type: "useCustomBaseCharge",
              })
            }
            displayText={(v) => renderAmount(v)}
          />
        )}
        {!useLegFlatRate && (
          <>
            <CalculationsLayout
              label="Fuel Surcharge"
              value={legFuelSurcharge}
              editable={true}
              id="fuelSurcharge"
              onSave={(saveData) =>
                handleCustomCharge({
                  id: renderId("fuelSurcharge"),
                  value: Number(saveData.value),
                  type: "useCustomFuelSurcharge",
                })
              }
              displayText={(v) => renderAmount(v)}
            />
            <CalculationsLayout
              label="Drop Charges"
              value={dropCharge}
              editable={true}
              id="dropCharge"
              onSave={(saveData) =>
                handleCustomCharge({
                  id: renderId("dropCharge"),
                  value: Number(saveData.value),
                  type: "useCustomDropCharge",
                })
              }
              displayText={(v) => renderAmount(v)}
            />
          </>
        )}
        <CalculationsLayout
          label="Other Charges"
          value={otherCharges}
          editable={true}
          id="otherCharges"
          onSave={(saveData) =>
            handleChange({
              id: renderId("otherCharges"),
              value: Number(saveData.value),
              type: "useCustomOtherCharges",
            })
          }
          displayText={(v) => renderAmount(v)}
        />
        <CalculationsLayout
          label="Total Charges"
          value={renderAmount(renderTotalCharges())}
        />
      </div>
      {/* <Metadata metadata={{}} /> */}
    </div>
  );
};

export const CalculationsLayout = ({
  label,
  value,
  editable,
  id,
  displayText,
  onSave,
  size,
}) => {
  return (
    <div className="flex flex-col space-y-1">
      <span className="block mb-2 text-sm font-medium text-neutral-700">
        {label}
      </span>
      {!editable ? (
        <span className="font-semibold text-xl">{value}</span>
      ) : (
        <EditableText
          className="ml-1"
          iconClasses="h-3 w-3"
          id={id}
          value={value}
          size={size}
          displayText={(v) => (
            <span className="font-semibold text-xl">{displayText(v)}</span>
          )}
          onSave={onSave}
          type="number"
        />
      )}
    </div>
  );
};
